import { useState, useEffect, useRef } from "react";
import { Send, CheckCircle2, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "../lib/api";

/* ─── helpers ─────────────────────────────── */
function genCaptcha() {
  const a = Math.floor(Math.random() * 9) + 1;
  const b = Math.floor(Math.random() * 9) + 1;
  return { a, b };
}

const inp = [
  "w-full bg-[#F8F9FA] border border-gray-200 rounded-lg px-3 py-2.5",
  "text-[#1A1A1A] text-sm focus:outline-none focus:border-[#DC2A1C]",
  "placeholder-gray-400 transition-colors",
].join(" ");

const sel = [
  "w-full bg-[#F8F9FA] border border-gray-200 rounded-lg px-3 py-2.5",
  "text-[#1A1A1A] text-sm focus:outline-none focus:border-[#DC2A1C]",
  "appearance-none cursor-pointer",
].join(" ");

function Label({ children, required }) {
  return (
    <label className="block text-[10px] uppercase tracking-[0.15em] font-semibold text-gray-400 mb-1">
      {children}{required && <span className="text-[#DC2A1C] ml-0.5">*</span>}
    </label>
  );
}

const EMPTY = { mode: "", type: "", inquiryText: "", name: "", email: "", phone: "", captchaAnswer: "" };

const MAX_FILE_SIZE_MB = 10;
const ALLOWED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "image/jpeg", "image/png", "image/gif", "image/webp",
  "text/plain",
];

/**
 * QuickInquiryForm
 *
 * Props:
 *   defaultMode  – "Air" | "Sea" | "" (pre-fills the Mode dropdown)
 *   compact      – bool (default true) — uses card shadow / compact padding
 */
export default function QuickInquiryForm({ defaultMode = "", compact = true }) {
  const [form, setForm] = useState({ ...EMPTY, mode: defaultMode });
  const [captcha, setCaptcha] = useState(genCaptcha);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [attachment, setAttachment] = useState(null); // { file, name, size }
  const fileRef = useRef(null);

  // If the parent changes defaultMode (e.g. navigating between service pages), update
  useEffect(() => {
    setForm((f) => ({ ...f, mode: defaultMode }));
  }, [defaultMode]);

  const change = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error("Unsupported file type. Please upload a PDF, Word, Excel, image or text file.");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      toast.error(`File too large. Maximum size is ${MAX_FILE_SIZE_MB} MB.`);
      e.target.value = "";
      return;
    }
    setAttachment({ file, name: file.name, size: (file.size / 1024).toFixed(0) + " KB" });
  };

  const removeAttachment = () => {
    setAttachment(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Name, email and phone are required.");
      return;
    }
    if (!form.inquiryText.trim()) {
      toast.error("Please enter your inquiry.");
      return;
    }
    if (parseInt(form.captchaAnswer, 10) !== captcha.a + captcha.b) {
      toast.error("Captcha answer is incorrect.");
      setCaptcha(genCaptcha());
      setForm((f) => ({ ...f, captchaAnswer: "" }));
      return;
    }

    setSending(true);
    try {
      // If attachment present, upload first and get a reference URL/name
      let attachmentUrl = "";
      if (attachment) {
        const fd = new FormData();
        fd.append("file", attachment.file);
        try {
          const res = await api.post("/attachments", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          attachmentUrl = res.data.url || "";
        } catch {
          // Attachment upload failed — proceed without it, note in details
          attachmentUrl = "";
          toast.warning("Attachment could not be uploaded. Sending without it.");
        }
      }

      const details = [
        form.inquiryText.trim(),
        attachment ? `\n[Attachment: ${attachment.name}${attachmentUrl ? ` — ${attachmentUrl}` : " (upload failed)"}]` : "",
      ].join("");

      await api.post("/quotes", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        service: [form.mode, form.type].filter(Boolean).join(" / ") || "General Inquiry",
        details,
        contact_method: "Email",
        inquiry_type: "quote",
        mode: form.mode || undefined,
        type: form.type || undefined,
        attachment_url: attachmentUrl || undefined,
        captcha_a: captcha.a,
        captcha_b: captcha.b,
        captcha_answer: captcha.a + captcha.b,
      });
      setSent(true);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setSent(false);
    setForm({ ...EMPTY, mode: defaultMode });
    setCaptcha(genCaptcha());
    setAttachment(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const cardStyle = compact
    ? {
        background: "#ffffff",
        borderRadius: 16,
        boxShadow: "0 4px 24px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)",
        border: "1px solid #f0f0f0",
        padding: "28px 24px",
      }
    : {
        background: "#ffffff",
        borderRadius: 16,
        border: "1px solid #e5e7eb",
        padding: "32px 28px",
      };

  if (sent) {
    return (
      <div style={cardStyle} className="flex flex-col items-center text-center py-10">
        <CheckCircle2 className="w-14 h-14 mb-4" style={{ color: "#DC2A1C" }} strokeWidth={1.5} />
        <h3 className="text-lg font-bold mb-2 text-[#1A1A1A]">Inquiry Sent!</h3>
        <p className="text-sm text-gray-500 mb-6">
          Our team will get back to you shortly.
        </p>
        <button
          onClick={reset}
          className="text-sm font-semibold px-5 py-2.5 rounded-lg border-2 transition-all"
          style={{ borderColor: "#DC2A1C", color: "#DC2A1C" }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#DC2A1C"; e.currentTarget.style.color = "#fff"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#DC2A1C"; }}
        >
          Send Another
        </button>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      {/* Header */}
      <div className="mb-5">
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold mb-1" style={{ color: "#DC2A1C" }}>
          Quick Inquiry
        </div>
        <h3 className="text-base font-bold text-[#1A1A1A]">Get a Quote</h3>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Mode + Type row (optional) */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Mode</Label>
            <div className="relative">
              <select name="mode" value={form.mode} onChange={change} className={sel}>
                <option value="">Any</option>
                <option value="Air">Air</option>
                <option value="Sea">Sea</option>
              </select>
              <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">▾</span>
            </div>
          </div>
          <div>
            <Label>Type</Label>
            <div className="relative">
              <select name="type" value={form.type} onChange={change} className={sel}>
                <option value="">Any</option>
                <option value="Import">Import</option>
                <option value="Export">Export</option>
              </select>
              <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">▾</span>
            </div>
          </div>
        </div>

        {/* Inquiry text */}
        <div>
          <Label required>Inquiry</Label>
          <textarea
            name="inquiryText"
            value={form.inquiryText}
            onChange={change}
            rows={4}
            placeholder="Describe your shipment — origin, destination, cargo type, dimensions, any special requirements…"
            required
            className={inp + " resize-none"}
          />
        </div>

        {/* File attachment */}
        <div>
          <Label>Attachment (optional)</Label>
          {attachment ? (
            <div
              className="flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm"
              style={{ background: "rgba(220,42,28,0.04)", borderColor: "rgba(220,42,28,0.25)" }}
            >
              <Paperclip className="w-4 h-4 flex-shrink-0" style={{ color: "#DC2A1C" }} />
              <span className="flex-1 truncate text-[#1A1A1A]" title={attachment.name}>{attachment.name}</span>
              <span className="text-xs text-gray-400 flex-shrink-0">{attachment.size}</span>
              <button type="button" onClick={removeAttachment} className="flex-shrink-0 ml-1">
                <X className="w-4 h-4 text-gray-400 hover:text-[#DC2A1C] transition-colors" />
              </button>
            </div>
          ) : (
            <label
              className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-dashed cursor-pointer transition-colors hover:border-[#DC2A1C]"
              style={{ borderColor: "#d1d5db", background: "#F8F9FA" }}
            >
              <Paperclip className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-400">Upload a file (PDF, Word, Excel, image — max 10 MB)</span>
              <input
                type="file"
                ref={fileRef}
                onChange={handleFile}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp,.txt"
                className="hidden"
              />
            </label>
          )}
        </div>

        {/* Name */}
        <div>
          <Label required>Full Name</Label>
          <input name="name" value={form.name} onChange={change} placeholder="Your name" required className={inp} />
        </div>

        {/* Email + Phone row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label required>Email</Label>
            <input type="email" name="email" value={form.email} onChange={change} placeholder="you@co.com" required className={inp} />
          </div>
          <div>
            <Label required>Phone</Label>
            <input type="tel" name="phone" value={form.phone} onChange={change} placeholder="+91…" required className={inp} />
          </div>
        </div>

        {/* Captcha */}
        <div>
          <Label required>Captcha — What is {captcha.a} + {captcha.b}?</Label>
          <input
            type="number"
            name="captchaAnswer"
            value={form.captchaAnswer}
            onChange={change}
            placeholder="Answer"
            required
            className={inp}
          />
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={sending}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold transition-all disabled:opacity-60"
          style={{ background: "#DC2A1C", color: "#ffffff" }}
          onMouseEnter={(e) => { if (!sending) e.currentTarget.style.background = "#b91c1c"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "#DC2A1C"; }}
        >
          {sending ? "Sending…" : <><Send className="w-4 h-4" /> Send Inquiry</>}
        </button>
      </form>
    </div>
  );
}
